import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Icon, type LucideIcon } from '../ui/Icon';

interface Props {
  icon: LucideIcon;
  label: string;
  disabled?: boolean;
  /** Panel width in Tailwind units; the stop list needs room for long titles. */
  widthClass?: string;
  children: (close: () => void) => ReactNode;
}

/**
 * A button of the selection bar that opens a panel above it. The bar sits at
 * the bottom of the screen, so the panel always drops up. Open/outside-click/
 * Escape handling mirrors `FilterMenu`.
 */
export default function SelectionPopover({ icon, label, disabled, widthClass = 'w-64', children }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex items-center gap-1.5 h-8 pl-2.5 pr-2 rounded-lg border text-sm font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default ${
          open
            ? 'border-teal-500 text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-950/40'
            : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600'
        }`}
      >
        <Icon as={icon} size={15} className={open ? 'text-teal-600' : 'text-slate-400'} />
        {label}
        <Icon as={ChevronDown} size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute right-0 bottom-full mb-2 z-30 ${widthClass} max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl overflow-hidden animate-fade-in`}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
